/**
 * Responsible for rendering an error within a form field behavior.
 * @module elements/fields/floatingError
 */
import $ from 'jquery';
import Symbol from 'es6-symbol';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import { EVENTS, TYPES } from './form-field-behaviors';
import { EVENTS as VALIDATOR_EVENTS } from './validator';
import random from '../../lib/random';

const { LOAD, BLUR, FOCUS, INVALID, INPUT, CHANGE, VALID } = EVENTS;
export { LOAD, BLUR, FOCUS, INVALID, INPUT, CHANGE, VALID };
const { CONTAINER, FIELD } = TYPES;
export { CONTAINER };
const { INVALID_SCROLL_POSITION } = VALIDATOR_EVENTS;
export { INVALID_SCROLL_POSITION };
const FLOATING_ERROR_OPTIONS = {
  floatingErrorContainerClassName: 'floating-label--floating-error',
  floatingErrorElement: 'p',
  floatingErrorClassName: 'floating-error',
};
const KEY = Symbol('error-el');
/**
 * Creates an error element to later on be appended to the DOM.
 * @private
 * @param {string} errorElement - Name of error element to create the element
 * @param {string} className - Optional class name applied when error is present
 * @param {string} message - Message used when an error has occurred
 * @returns {Element} An element containing the floating error
 */
const createErrorEl = (errorElement, id, className, message) => {
  const el = document.createElement(errorElement);
  if (className) {
    el.classList.add(className);
  }
  el.id = id;
  el.textContent = ` ${message}`;

  el.prepend($(`
    <span class="icon icon--xs icon--exclamation-point">
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 15.2A7.17 7.17 0 0 1 .8 8 7.17 7.17 0 0 1 8 .8 7.17 7.17 0 0 1 15.2 8 7.17 7.17 0 0 1 8 15.2zm0-5.6c.4 0 .6-.3.6-.7l.3-4.3c0-.4-.4-.7-.8-.7s-1 .2-1 .7l.3 4.3c0 .4.2.7.6.7z"/><circle cx="8" cy="11.3" r=".9"/>
        </svg>
    </span>
  `)[0]);

  return el;
};
/**
 * Renders an error element into the DOM.
 * @private
 * @param {HTMLElement} container - The container with the label/form field
 * @param {string} errorElement - Name of error element to create the element
 * @param {string} className - Optional class name applied when error is present
 * @param {string} message - Message used when an error has occurred
 * @returns {HTMLElement} - The error element
 */
const renderErrorEl = (container, errorElement, id, className, message) => {
  const errorEl = createErrorEl(errorElement, id, className, message);
  container.appendChild(errorEl);
  return errorEl;
};
/**
 * Responsible for removing an error element if it exists.
 * @private
 * @param {HTMLElement} container - The container with the label/form field
 * @param {HTMLElement} errorElement - Element containing the error
 * @param {Map} state - Contains state info around error
 * @param {string} className - className interacting with container
 */
const removeErrorEl = (container, errorElement, state, className) => {
  if (errorElement) {
    container.classList.remove(className);
    if (errorElement.parentNode) {
      errorElement.parentNode.removeChild(errorElement);
      state.delete(KEY);
    }
  }
};
/**
 * Sets up a listener for when an invalid formField has been found so it can
 * set the position of where to scroll to in the DOM.
 * @private
 * @param {HTMLElement} formField - The form field element
 */
const listenForInvalid = (formField) => {
  formField.addEventListener(INVALID_SCROLL_POSITION, ({ detail: { delegateScroll } }) => {
    delegateScroll((highestEl) => {
      highestEl.scrollIntoView({
        block: 'center',
      });
    });
  });
};
/**
 * Responsibility for resolving a field id for the purpose of uniquely
 * identifying an aria-describedby.
 * @param {HTMLElement} el - Form element receiving error
 * @returns {string} The id assigned to the field if one doesn't exist
 */
const resolveFieldId = (el) => {
  const ariaIdAttr = 'aria-describedby';
  const existingId = el.getAttribute(ariaIdAttr);
  if (existingId) {
    return existingId;
  }
  const elements = get(el, 'form.elements', []);
  const index = Array.from(elements).findIndex(formEl => formEl === el);
  const suffix = isNumber(index) ? index : random();
  const id = random({ prefix: `err-${suffix}` });
  el.setAttribute(ariaIdAttr, id);
  return id;
};
/**
 * Responds to any errors that occur on a form field by rendering a
 * "floating error" or an error that accompanies the field in question.
 * @private
 * @param {string} type - Type of event
 * @param {HTMLElement} target - Element receiving the event
 * @param {HTMLElement} container - The container with the label/form field
 * @param {Map} state - Acts as a state container
 * @param {boolean} validateOnlyOnSubmit - Instructs to only validate on submit
 * @param {string} containerClassName - Class name appended to the container if
 * an error occurs
 * @param {string} errorElement - Name of error element to create the element
 * @param {string} className - Optional class name applied when error is present
 * @param {string} message - Message used when an error has occurred
 */
const respond = (type, target, container, state, validateOnlyOnSubmit, {
  floatingErrorContainerClassName: containerClassName,
  floatingErrorElement: errorElement,
  floatingErrorClassName: className,
  floatingErrorMessage: message = '',
}) => {
  const errorEl = state.get(KEY);
  const errorMessage = message || target.validationMessage;
  let fieldId;

  switch (type) {
    case INPUT:
      // The error message should stick around when the user focuses on the
      // element. However, when the user interacts with the element, we want to
      // remove the error, if it exists. Therefore the error message will be
      // announced to the user upon focusing without the need of having to
      // accounce every time the element has been interacted with if it's
      // considered invalid.
      if (state.get(KEY)) {
        removeErrorEl(container, errorEl, state, containerClassName);
      }
      break;
    case LOAD:
    case BLUR:
    case CHANGE:
      if (!validateOnlyOnSubmit) {
        // Assume there is no error for a clean slate
        removeErrorEl(container, errorEl, state, containerClassName);
        // This may feel redundant since there's a chance the form element
        // may already have an error. However, in the case where the blur
        // event only occurs or where the floating error is the only listener,
        // then this is necessary. Otherwise, it is redundant, but it doesn't
        // hurt anything or have any performance issues to reset the validity.
        if (container.querySelector(':invalid') === target) {
          target.setCustomValidity(errorMessage);
        }
        target.checkValidity();
      }

      break;
    case VALID:
      removeErrorEl(container, errorEl, state, containerClassName);

      break;
    case INVALID:
      removeErrorEl(container, errorEl, state, containerClassName);
      // Supercedes the applied validity for one that may be more relevant
      // to the field.
      target.setCustomValidity(errorMessage);
      container.classList.add(containerClassName);
      fieldId = resolveFieldId(target);
      state.set(KEY, renderErrorEl(container, errorElement, fieldId, className, errorMessage));
      break;
    default:
      break;
  }
};
/**
 * Responsible for rendering an error message when an error occurs on a form
 * field. To avoid any weirdness with CSS positioning, the error message is
 * rendered to the screen around the field or floats hence a floating error.
 *
 * In order to activate this functionality, the container must contain the
 * following data attribute:
 *
 * - data-floating-error-message
 *
 * Other options include:
 *
 * - `data-floating-error-element`: The name of the element used for the floating
 *   error. Defaults to a `p` (for paragraph).
 * - `data-floating-error-class-name`: An additional className added to the
 *   floating error message element. Defaults to `''`.
 * - `data-floating-container-class-name`: A className applied to the container
 *   container when the floating error is shown. Defaults to `floating-label--floating-error`.
 *
 * @example
 * <div class="floating-label" data-floating-error-message="Hey dummy!">
 *  <label for="dude" class="floating-label__label">Something</label>
 *  <input
 *    required
 *    id="dude"
 *    type="text"
 *    class="floating-label__input"
 *    placeholder="Enter something">
 * </div>
 * @see FLOATING_ERROR_OPTIONS
 * @param {Object} options - Configurable options
 * @returns {Function} Used to setup and respond to events from the form field
 * behavior
 */
const floatingError = (options = {}) => (...nodes) => {
  const [{ node: container }] = nodes.filter(({ type }) => type === CONTAINER);
  const [{ node: formField }] = nodes.filter(({ type }) => type === FIELD);
  const opts = Object.assign({}, FLOATING_ERROR_OPTIONS, options, $(container).data());
  const state = new Map();
  if (container.dataset.disableFloatingError) {
    return () => { };
  }
  listenForInvalid(formField, state);
  return (type, { target }, { validateOnlyOnSubmit }) =>
    respond(type, target, container, state, validateOnlyOnSubmit, opts);
};
export default floatingError;



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/floating-error.js